import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Spinner,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus, Trash2, FileText } from 'lucide-react';
import apiService from '../services/api';
import { Clienti, Prestazione } from '../types';
import { useSpecialty } from '../contexts/SpecialtyContext';

interface NuovaFatturaFormData {
  numero_fattura: string;
  data: string;
  tipo_pagamento: 'POS' | 'Contanti' | 'Bonifico';
  bollo?: string;
  totale: string;
  cliente_id?: number;
  nome: string;
  cognome: string;
  indirizzo: string;
  citta: string;
  codice_fiscale: string;
  email: string;
  prestazioni: Array<{
    id: number;
    prestazione: string;
  }>;
}

const NuovaFattura: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSpecialty } = useSpecialty();
  
  // Se non c'è una specialistica selezionata, reindirizza alla selezione
  React.useEffect(() => {
    if (!selectedSpecialty) {
      navigate('/specialty-selection');
    }
  }, [selectedSpecialty, navigate]);
  
  // State per ricerca cliente
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Clienti[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<Clienti | null>(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // State per ricerca prestazioni
  const [prestazioniRows, setPrestazioniRows] = useState<Array<{
    id: string;
    value: string;
    prestazioni: Prestazione[];
    showResults: boolean;
  }>>([{ id: '1', value: '', prestazioni: [], showResults: false }]);
  
  // State per form
  const [formData, setFormData] = useState<NuovaFatturaFormData>({
    numero_fattura: '',
    data: new Date().toISOString().split('T')[0],
    tipo_pagamento: 'POS',
    bollo: '',
    totale: '',
    nome: '',
    cognome: '',
    indirizzo: '',
    citta: '',
    codice_fiscale: '',
    email: '',
    prestazioni: []
  });
  
  const [isChecked, setIsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingNextNumber, setLoadingNextNumber] = useState(false);

  // Carica automaticamente il prossimo numero di fattura al mount del componente
  useEffect(() => {
    const loadNextInvoiceNumber = async () => {
      setLoadingNextNumber(true);
      try {
        const response = await apiService.getNextInvoiceNumber();
        console.log('Prossimo numero fattura ricevuto:', response);
        setFormData(prev => ({
          ...prev,
          numero_fattura: response.next_invoice_number.toString()
        }));
      } catch (error) {
        console.error('Errore nel caricamento del prossimo numero fattura:', error);
        toast.error('Errore nel caricamento del numero fattura automatico');
      } finally {
        setLoadingNextNumber(false);
      }
    };

    loadNextInvoiceNumber();
  }, []);

  // Gestione ricerca clienti (stessa logica del NuovoReferto)
  const handleClientSearch = async (query: string) => {
    setSearchQuery(query);
    
    // Se c'è un cliente selezionato e il testo non corrisponde più, resettalo
    if (selectedCliente && query !== `${selectedCliente.nome} ${selectedCliente.cognome}`) {
      setSelectedCliente(null);
      setFormData(prev => ({
        ...prev,
        cliente_id: undefined,
        nome: '',
        cognome: '',
        indirizzo: '',
        citta: '',
        codice_fiscale: '',
        email: ''
      }));
    }
    
    if (query.length > 2) {
      try {
        const response = await apiService.searchCliente(query);
        console.log('Risposta ricerca clienti:', response);
        
        // Gestisci la struttura paginata della risposta API
        let clientiResults: Clienti[] = [];
        if (response && typeof response === 'object' && 'results' in response) {
          // Struttura paginata: estrai i clienti da response.results[].cliente
          clientiResults = response.results?.map((item: any) => item.cliente) || [];
        } else if (Array.isArray(response)) {
          // Array semplice di clienti
          clientiResults = response;
        }
        
        setSearchResults(clientiResults);
        setShowSearchResults(true);
        console.log('Clienti estratti per il dropdown:', clientiResults);
      } catch (error) {
        console.error('Errore nella ricerca clienti:', error);
        setSearchResults([]);
        setShowSearchResults(false);
      }
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const selectCliente = (cliente: Clienti) => {
    setSelectedCliente(cliente);
    setSearchQuery(`${cliente.nome} ${cliente.cognome}`);
    setShowSearchResults(false);
    setFormData(prev => ({
      ...prev,
      cliente_id: cliente.id,
      nome: cliente.nome || '',
      cognome: cliente.cognome || '',
      indirizzo: cliente.indirizzo || '',
      citta: cliente.citta || '',
      codice_fiscale: cliente.codice_fiscale || '',
      email: cliente.email || ''
    }));
  };

  // Gestione ricerca prestazioni
  const handlePrestazioneSearch = async (query: string, rowIndex: number) => {
    if (query.length > 2) {
      try {
        const response = await apiService.searchPrestazione(query);
        console.log('Risposta ricerca prestazioni:', response);
        
        setPrestazioniRows(prev => prev.map((row, index) => 
          index === rowIndex 
            ? { ...row, prestazioni: response || [], showResults: true }
            : row
        ));
      } catch (error) {
        console.error('Errore nella ricerca prestazioni:', error);
        setPrestazioniRows(prev => prev.map((row, index) => 
          index === rowIndex 
            ? { ...row, prestazioni: [], showResults: false }
            : row
        ));
      }
    } else {
      setPrestazioniRows(prev => prev.map((row, index) => 
        index === rowIndex 
          ? { ...row, prestazioni: [], showResults: false }
          : row
      ));
    }
  };

  const selectPrestazione = (prestazione: Prestazione, rowIndex: number) => {
    setPrestazioniRows(prev => prev.map((row, index) => 
      index === rowIndex 
        ? { 
            ...row, 
            value: prestazione.prestazione, 
            id: prestazione.id.toString(),
            prestazioni: [],
            showResults: false
          }
        : row
    ));
  };

  // Gestione form
  const handleInputChange = (field: keyof NuovaFatturaFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Aggiungi riga prestazione
  const addPrestazioneRow = () => {
    const newId = (prestazioniRows.length + 1).toString();
    setPrestazioniRows(prev => [...prev, { 
      id: newId, 
      value: '', 
      prestazioni: [], 
      showResults: false 
    }]);
  };

  // Rimuovi riga prestazione
  const removePrestazioneRow = (rowIndex: number) => {
    if (prestazioniRows.length > 1) {
      setPrestazioniRows(prev => prev.filter((_, index) => index !== rowIndex));
    }
  };

  // Genera fattura
  // Se non c'è specialistica selezionata, non mostrare nulla
  if (!selectedSpecialty) {
    return <div className="text-center p-4">Reindirizzamento...</div>;
  }

  const handleGenerateFattura = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCliente && !formData.nome) {
      toast.error('Seleziona un cliente o inserisci i dati del cliente');
      return;
    }

    if (!formData.numero_fattura || !formData.data || !formData.totale) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setSubmitting(true);
    try {
      // Ottieni l'ID della specialità dal context
      console.log('Specialità selezionata:', selectedSpecialty);
      if (!selectedSpecialty) {
        toast.error('Specialità non selezionata');
        return;
      }

      // Prepara i dati della fattura secondo il nuovo formato
      const prestazioniIds = prestazioniRows
        .filter(row => row.id && row.value)
        .map(row => parseInt(row.id));

      const fatturaData = {
        data: formData.data,
        prestazioni: prestazioniIds,
        totale: formData.totale,
        numero_fattura: formData.numero_fattura,
        specialistica: parseInt(selectedSpecialty),
        tipo_pagamento: formData.tipo_pagamento,
        bollo: isChecked ? formData.bollo : undefined,
        // Se c'è un cliente selezionato, manda solo l'ID
        ...(selectedCliente ? {
          cliente_id: selectedCliente.id
        } : {
          // Altrimenti manda i dati del nuovo cliente
          nome: formData.nome,
          cognome: formData.cognome,
          indirizzo: formData.indirizzo,
          citta: formData.citta,
          codice_fiscale: formData.codice_fiscale,
          email: formData.email
        })
      };

      console.log('Dati fattura da inviare:', fatturaData);
      
      await apiService.createFattura(fatturaData);
      
      toast.success('Fattura generata con successo');
      // Naviga alla lista fatture della specialistica corrente
      navigate(`/fatture/${selectedSpecialty}`);
    } catch (error) {
      console.error('Errore durante la generazione della fattura:', error);
      toast.error('Errore durante la generazione della fattura');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4 page-banner">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="outline-primary"
              className="btn-back"
              title="Torna alle Fatture"
              aria-label="Torna alle Fatture"
              onClick={() => navigate('/fatture')}
            >
              <ArrowLeft />
            </Button>
          </div>
          <h2>Nuova Fattura</h2>
        </Col>
      </Row>

      <Form onSubmit={handleGenerateFattura}>
        <Row>
          <Col lg={8}>
            {/* Sezione Cliente */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Dati Cliente</h5>
              </Card.Header>
              <Card.Body>
                {/* Ricerca Cliente */}
                <div className="mb-4">
                  <Form.Group className="position-relative">
                    <Form.Label>Cerca Cliente</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Cerca tra i tuoi clienti..."
                      value={searchQuery}
                      onChange={(e) => handleClientSearch(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                    />
                    {showSearchResults && searchResults.length > 0 && (
                      <div 
                        className="position-absolute w-100 bg-white border rounded shadow" 
                        style={{ top: '100%', zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
                      >
                        <ListGroup variant="flush">
                          {searchResults.map((cliente) => (
                            <ListGroup.Item
                              key={cliente.id}
                              action
                              onClick={() => selectCliente(cliente)}
                              className="d-flex justify-content-between align-items-center"
                              style={{ cursor: 'pointer' }}
                            >
                              <span><strong>{cliente.nome} {cliente.cognome}</strong></span>
                              <Badge bg="secondary">{cliente.email || 'N/D'}</Badge>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                  </Form.Group>
                </div>

                {/* Dati Cliente */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nome *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.nome}
                        onChange={(e) => handleInputChange('nome', e.target.value)}
                        placeholder="Nome"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cognome *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.cognome}
                        onChange={(e) => handleInputChange('cognome', e.target.value)}
                        placeholder="Cognome"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Indirizzo *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.indirizzo}
                        onChange={(e) => handleInputChange('indirizzo', e.target.value)}
                        placeholder="Indirizzo"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Città *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.citta}
                        onChange={(e) => handleInputChange('citta', e.target.value)}
                        placeholder="Città"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Codice Fiscale *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.codice_fiscale}
                        onChange={(e) => handleInputChange('codice_fiscale', e.target.value)}
                        placeholder="Codice Fiscale"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Email"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Sezione Prestazioni */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Prestazioni</h5>
              </Card.Header>
              <Card.Body>
                {prestazioniRows.map((row, index) => (
                  <div key={row.id} className="mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div className="flex-grow-1 position-relative">
                        <Form.Control
                          type="text"
                          placeholder="Cerca la prestazione"
                          value={row.value}
                          onChange={(e) => {
                            setPrestazioniRows(prev => prev.map((r, i) => 
                              i === index ? { ...r, value: e.target.value } : r
                            ));
                            handlePrestazioneSearch(e.target.value, index);
                          }}
                        />
                        {row.showResults && row.prestazioni.length > 0 && (
                          <div 
                            className="position-absolute w-100 bg-white border rounded shadow" 
                            style={{ top: '100%', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                          >
                            <ListGroup variant="flush">
                              {row.prestazioni.map((prestazione) => (
                                <ListGroup.Item
                                  key={prestazione.id}
                                  action
                                  onClick={() => selectPrestazione(prestazione, index)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {prestazione.prestazione}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </div>
                        )}
                      </div>
                      {prestazioniRows.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removePrestazioneRow(index)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="text-center">
                  <Button
                    variant="outline-primary"
                    onClick={addPrestazioneRow}
                    className="rounded-pill"
                  >
                    <Plus className="me-2" />
                    Aggiungi prestazione
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Sezione Dettagli Fattura */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Dettagli Fattura</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Numero Fattura *
                        {loadingNextNumber && (
                          <Spinner size="sm" className="ms-2" />
                        )}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.numero_fattura}
                        onChange={(e) => handleInputChange('numero_fattura', e.target.value)}
                        placeholder={loadingNextNumber ? "Caricamento numero automatico..." : "Numero Fattura"}
                        required
                        disabled={loadingNextNumber}
                      />
                      {loadingNextNumber && (
                        <Form.Text className="text-muted">
                          Caricamento del prossimo numero di fattura disponibile...
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data *</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.data}
                        onChange={(e) => handleInputChange('data', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Modalità di pagamento *</Form.Label>
                      <Form.Select
                        value={formData.tipo_pagamento}
                        onChange={(e) => handleInputChange('tipo_pagamento', e.target.value as 'POS' | 'Contanti' | 'Bonifico')}
                        required
                      >
                        <option value="POS">POS</option>
                        <option value="Contanti">Contanti</option>
                        <option value="Bonifico">Bonifico</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="flexCheckDefault"
                        label="Marca da bollo?"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  {isChecked && (
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Numero marca da bollo</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.bollo || ''}
                          onChange={(e) => handleInputChange('bollo', e.target.value)}
                          placeholder="Numero marca da bollo"
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Totale Fattura *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={formData.totale}
                        onChange={(e) => handleInputChange('totale', e.target.value)}
                        placeholder="Totale Fattura"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Pulsante Genera Fattura */}
            <Card>
              <Card.Body className="text-center">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={submitting}
                  className="w-100"
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generazione in corso...
                    </>
                  ) : (
                    <>
                      <FileText className="me-2" />
                      Genera Fattura
                    </>
                  )}
                </Button>
                <small className="text-muted d-block mt-2">
                  Genera la fattura finale dopo aver completato tutti i campi
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default NuovaFattura;
